import { useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  CalendarClock,
  CalendarRange,
  LayoutDashboard,
  Users,
  CreditCard,
  BarChart3,
  LogOut,
  ChevronLeft,
  ChevronDown,
  DollarSign,
  Tag,
  Package,
  Megaphone,
  GraduationCap,
  ListTree,
  ScrollText,
  FileQuestion,
  ClipboardList,
  ClipboardCheck,
  ListChecks,
  Library,
  Building2,
  Tags,
  Stethoscope,
  Target,
  Goal,
  Inbox,
  Clapperboard,
  MonitorPlay,
  Video,
  MessageSquareText,
  KeyRound,
  Sparkles,
  Settings,
  SlidersHorizontal,
  Mic,
  FilePen,
  ShieldCheck,
  Boxes,
  Link2,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/authStore'
import { ADMIN_PROFILES_PATH, canAccessAdminPath } from '@/lib/adminSections'
import { EXAM_AREAS_COPY } from '@/lib/copy/examAreas'
import { OBJECTIVE_COPY } from '@/lib/copy/objective'
import { SALES_LINK_COPY } from '@/lib/copy/salesLinks'
import type { User } from '@/types/user'
import { useUiStore } from '@/stores/uiStore'
import { UserAvatar } from './UserAvatar'

interface NavChild {
  name: string
  href: string
  icon: LucideIcon
  /** Rota que e prefixo das irmas (ex.: /provas-integra vs /provas-integra/questoes). */
  end?: boolean
}

interface NavItem {
  name: string
  icon: LucideIcon
  /** Ausente em item que so agrupa: clicar abre/fecha o submenu. */
  href?: string
  children?: NavChild[]
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
  { name: 'Usuários', href: '/admin/users', icon: Users },
  {
    name: 'Mentorias',
    href: '/admin/mentorships',
    icon: Users,
    // R10-24: numero de 1:1, duracao media e frequencia por madrinha.
    children: [{ name: 'Métricas das 1:1', href: '/admin/mentorships/metricas', icon: BarChart3 }],
  },
  { name: 'Assinaturas', href: '/admin/subscriptions', icon: CreditCard },
  { name: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
  { name: 'Receita', href: '/admin/revenue', icon: DollarSign },
  // R12-05: "Quizzes" (/admin/quizzes) e "Cursos" (/admin/courses) sao a estrutura antiga
  // e sairam do menu — so do menu: as rotas continuam, com aviso apontando para Videoaulas.
  {
    name: 'Cursinhos',
    href: '/admin/prep-courses',
    icon: GraduationCap,
    // Aulas pertencem a um cursinho, entao entram como submenu em vez de item raiz.
    children: [
      { name: 'Aulas', href: '/admin/course-lessons', icon: ListTree, end: true },
      // r6-32: subespecialidade e topico para varias aulas de uma vez.
      { name: 'Vincular em lote', href: '/admin/course-lessons/vincular', icon: Link2 },
      // Topicos que alunos tiraram/puseram numa aula ao registrar o estudo.
      { name: 'Ajustes de tópicos', href: '/admin/lesson-topic-adjustments', icon: SlidersHorizontal },
    ],
  },
  {
    // Grupo sem pagina propria: o catalogo esta em "Provas" e as questoes so
    // existem dentro de uma prova.
    name: 'Provas na Íntegra',
    icon: ScrollText,
    children: [
      { name: 'Provas', href: '/admin/provas-integra', icon: ClipboardList, end: true },
      { name: 'Questões', href: '/admin/provas-integra/questoes', icon: ListChecks },
      // R10-20: provas que alunos pediram e o catalogo nao tem (aceitas vao para o Hermes).
      { name: 'Pedidos de prova', href: '/admin/provas-integra/pedidos', icon: FileQuestion },
      // R10-05: instituicoes que os alunos priorizam x temos prova?
      { name: 'Demanda de provas', href: '/admin/provas-integra/demanda', icon: Target },
    ],
  },
  {
    // Catalogos de referencia. Nao ficam sob "Provas na Integra" porque Especialidade
    // tambem e usada por Cursinhos/Aulas, estudos e revisoes.
    name: 'Catálogos',
    icon: Library,
    // Ordem = as relacoes do catalogo, do mais amplo para o mais especifico:
    //   Instituicao N-N ProcessoSeletivo N-N Especialidade 1-N Subespecialidade 1-N Topico
    // Cada um e uma tabela: institutions, selection_processes, specialties,
    // sub_specialties e topics. Os tres ultimos formam a arvore do catalogo.
    // R12-07: "Instituicoes (provas)" = institutions, as bancas das provas e dos processos
    // seletivos. A faculdade de graduacao do aluno e outra tabela (graduation_institutions),
    // sem relacao com essa arvore.
    // "Topicos" aqui e `topics`, nao o `canonical_topics` do FSRS — que nao tem tela.
    children: [
      { name: 'Instituições (provas)', href: '/admin/institutions', icon: Building2 },
      { name: 'Faculdades de graduação', href: '/admin/graduation-institutions', icon: GraduationCap },
      { name: 'Processos Seletivos', href: '/admin/selection-processes', icon: ClipboardCheck },
      // R08-26: prazos do edital por instituicao (fila do aluno e sinal do padrinho).
      { name: 'Calendário de provas', href: '/admin/exam-deadlines', icon: CalendarClock },
      // HERMES: cronograma dos processos seletivos (revisoes do agente + cadastro manual).
      { name: 'Cronogramas de processos', href: '/admin/exam-processes', icon: CalendarRange },
      // r6-23: especialidades pretendidas (o que o aluno quer ser), N-N com o processo; nao
      // e a arvore. R12-04: na tela, "Especialidades medicas" (career_goals) e "Grandes
      // areas" (specialties, o que a prova cobra) — os dois nomes vem de lib/copy.
      { name: OBJECTIVE_COPY.catalogTitle, href: '/admin/career-goals', icon: Goal },
      { name: EXAM_AREAS_COPY.title, href: '/admin/specialties', icon: Stethoscope },
      { name: 'Subespecialidades', href: '/admin/sub-specialties', icon: Tags },
      { name: 'Tópicos', href: '/admin/topics', icon: Target },
      // R5: o que os alunos pediram e o catalogo acima ainda nao tem.
      { name: 'Correções', href: '/admin/catalog-corrections', icon: Inbox },
    ],
  },
  // Nao entra em "Provas na Integra": a condicao de hoje e a nota de prova, mas
  // as proximas (estudo, revisao) nao tem nada a ver com prova.
  { name: 'Vídeos de Acompanhamento', href: '/admin/followup-videos', icon: Clapperboard },
  // Spec r4/12 (R1): videoaulas de CURSO sao outra entidade e outra tela — cursos com
  // aulas do Vimeo liberadas por condicao, o que a aba Video Aulas do aluno lista.
  { name: 'Videoaulas', href: '/admin/video-courses', icon: MonitorPlay },
  // ZOOM: aulas ao vivo (webinars da Zoom), presenca e gravacao.
  { name: 'Reuniões ao vivo', href: '/admin/aulas-ao-vivo', icon: Video },
  // R10-14: pacotes de features do instrutor (o padrao + extras como Zoom).
  { name: 'Pacotes do instrutor', href: '/admin/pacotes-do-instrutor', icon: Boxes },
  // Fica ao lado dos videos porque e a mesma conversa: o relato e uma das condicoes que
  // libera video. Somente leitura — nao ha edicao nem exclusao.
  { name: 'Relatos de Dificuldade', href: '/admin/relatos-de-dificuldade', icon: MessageSquareText },
  // r7-20: produto e cupom formam o link do checkout (/checkout?produto=&cupom=).
  { name: 'Planos', href: '/admin/planos', icon: Package },
  { name: 'Cupons', href: '/admin/coupons', icon: Tag },
  // VENDA-02: link de uso unico com Pix + cartoes.
  { name: SALES_LINK_COPY.plural, href: '/admin/links-de-venda', icon: Link2 },
  { name: 'Anúncios', href: '/admin/announcements', icon: Megaphone },
  {
    // Grupo sem pagina propria (mesmo padrao de "Catalogos"). As URLs dos filhos sao as de
    // antes do grupo existir — so a Zoom nasce sob /admin/configuracoes/.
    name: 'Configurações',
    icon: Settings,
    children: [
      // MH9-01: chaves dos agentes externos (Hermes) — gerar, escopos, revogar.
      { name: 'Chaves de API', href: '/admin/chaves-de-api', icon: KeyRound },
      // MH9-13: assistente de IA do instrutor — quem pode usar e a config com historico.
      { name: 'Assistente de IA', href: '/admin/assistente-ia', icon: Sparkles },
      // ZOOM: credenciais da Zoom editadas no admin (substitui o .env).
      { name: 'Zoom', href: '/admin/configuracoes/zoom', icon: Video },
      // TRANSC-04: transcricao das 1:1 (provedor, modelo, chaves) e painel do piloto.
      { name: 'Transcritor', href: '/admin/configuracoes/transcritor', icon: Mic },
      // R12-02: contratos do aluno (um PDF por plano) assinados pelo Autentique.
      { name: 'Contratos do aluno', href: '/admin/configuracoes/contrato-do-aluno', icon: FilePen },
      // R10-15: secoes do admin por perfil de acesso (so admin sem perfil ve este item).
      { name: 'Perfis de acesso', href: ADMIN_PROFILES_PATH, icon: ShieldCheck },
    ],
  },
]

/**
 * R10-15: o menu do perfil de acesso. O filtro e pelo `href`, com a mesma tabela das rotas
 * (`lib/adminSections`): item novo sem linha la aparece so para admin sem perfil. Grupo
 * sem nenhum filho liberado some inteiro.
 */
function visibleNavigation(items: NavItem[], user: User | null): NavItem[] {
  return items.flatMap((item) => {
    const allowed = item.children?.filter((child) => canAccessAdminPath(user, child.href))
    const children = allowed?.length ? allowed : undefined
    const ownAllowed = item.href ? canAccessAdminPath(user, item.href) : false
    if (!ownAllowed && !children) return []
    // Pai com pagina propria fora do perfil (mas com filho liberado) vira grupo sem link.
    if (item.href && !ownAllowed) return [{ ...item, href: undefined, children }]
    return [{ ...item, children }]
  })
}

const isChildActive = (child: NavChild, pathname: string) =>
  child.end ? pathname === child.href : pathname === child.href || pathname.startsWith(`${child.href}/`)

export default function AdminSidebar() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { logout, user } = useAuthStore()
  const { sidebarCollapsed: isCollapsed, toggleSidebar } = useUiStore()

  // Submenus expansiveis. O estado padrao e derivado da rota — o grupo da pagina
  // atual aparece aberto — e `overrides` guarda apenas o que o usuario clicou.
  // Derivar em vez de sincronizar por efeito evita render em cascata a cada
  // navegacao, e abrir um grupo nao fecha os outros.
  const [overrides, setOverrides] = useState<Record<string, boolean>>({})

  const hasActiveChild = (item: NavItem) =>
    item.children?.some((child) => isChildActive(child, pathname)) ?? false

  const isGroupOpen = (item: NavItem) => overrides[item.name] ?? hasActiveChild(item)

  const toggleGroup = (item: NavItem) => {
    // Recolhida, a sidebar so mostra icones e nao ha espaco para o submenu:
    // abrir o grupo implica reabrir a sidebar.
    if (isCollapsed) toggleSidebar()

    setOverrides({ ...overrides, [item.name]: !isGroupOpen(item) })
  }

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const linkClasses = (isActive: boolean) =>
    cn(
      'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
      isActive ? 'bg-primary-500/20 text-primary-400' : 'text-neutral-300 hover:bg-neutral-700 hover:text-white',
    )

  return (
    <>
      {/* Mobile overlay */}
      <div className="lg:hidden fixed inset-0 z-40 bg-black/50 hidden" />

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-neutral-800 border-r border-neutral-700 transition-all duration-300',
          isCollapsed ? 'w-16' : 'w-64'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-neutral-700">
          {!isCollapsed && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                <span className="text-white font-bold text-sm">MH</span>
              </div>
              <div>
                <span className="font-semibold text-white">Admin</span>
                <span className="text-xs text-neutral-400 block">MedHunter</span>
              </div>
            </div>
          )}
          <button
            onClick={toggleSidebar}
            className="p-1.5 rounded-lg hover:bg-neutral-700 text-neutral-400 hover:text-white transition-colors"
          >
            <ChevronLeft className={cn('h-5 w-5 transition-transform', isCollapsed && 'rotate-180')} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {visibleNavigation(navigation, user).map((item) => {
            const isOpen = isGroupOpen(item)

            return (
              <div key={item.name}>
                {item.href ? (
                  <div className="flex items-center">
                    <NavLink to={item.href} className={({ isActive }) => cn(linkClasses(isActive), 'flex-1 min-w-0')}>
                      <item.icon className="h-5 w-5 flex-shrink-0" />
                      {!isCollapsed && <span className="truncate">{item.name}</span>}
                    </NavLink>
                    {item.children && !isCollapsed && (
                      <button
                        onClick={() => toggleGroup(item)}
                        className="p-1.5 ml-1 rounded-lg text-neutral-400 hover:bg-neutral-700 hover:text-white transition-colors"
                        aria-expanded={isOpen}
                        aria-label={`${isOpen ? 'Recolher' : 'Expandir'} ${item.name}`}
                      >
                        <ChevronDown className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => toggleGroup(item)}
                    className={cn(linkClasses(hasActiveChild(item)), 'w-full', isCollapsed && 'justify-center')}
                    aria-expanded={isOpen}
                    title={isCollapsed ? item.name : undefined}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    {!isCollapsed && (
                      <>
                        <span className="flex-1 text-left truncate">{item.name}</span>
                        <ChevronDown className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
                      </>
                    )}
                  </button>
                )}

                {/* Submenu. Recolhido, a sidebar so mostra icones — sem indentacao,
                    o filho ficaria indistinguivel do pai, entao nem o exibimos. */}
                {isOpen && !isCollapsed &&
                  item.children?.map((child) => (
                    <NavLink
                      key={child.name}
                      to={child.href}
                      end={child.end}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 py-2 pl-9 pr-3 rounded-lg text-sm transition-colors mt-1',
                          isActive
                            ? 'bg-primary-500/20 text-primary-400 font-medium'
                            : 'text-neutral-400 hover:bg-neutral-700 hover:text-white'
                        )
                      }
                    >
                      <child.icon className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{child.name}</span>
                    </NavLink>
                  ))}
              </div>
            )
          })}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-neutral-700">
          {!isCollapsed && (
            <div className="flex items-center gap-3 mb-3">
              <UserAvatar
                avatarUrl={user?.avatar_url}
                initial={user?.full_name?.charAt(0).toUpperCase() || 'A'}
                className="w-8 h-8"
                fallbackClassName="bg-primary-500/20 text-primary-400 font-medium text-sm"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
                <p className="text-xs text-neutral-400 truncate">{user?.email}</p>
              </div>
            </div>
          )}

          <button
            onClick={() => navigate('/app/dashboard')}
            className={cn(
              'flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm text-neutral-300 hover:bg-neutral-700 hover:text-white transition-colors mb-2',
              isCollapsed && 'justify-center'
            )}
          >
            <LayoutDashboard className="h-5 w-5" />
            {!isCollapsed && <span>Voltar ao App</span>}
          </button>

          <button
            onClick={handleLogout}
            className={cn(
              'flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/10 transition-colors',
              isCollapsed && 'justify-center'
            )}
          >
            <LogOut className="h-5 w-5" />
            {!isCollapsed && <span>Sair</span>}
          </button>
        </div>
      </aside>
    </>
  )
}
